import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Api } from '@carrier/utils/Api';
import Papa from 'papaparse';
import { formatInTimeZone } from 'date-fns-tz';

export const fetchForms = createAsyncThunk(
  'forms/fetchForms',
  async (params, { getState, rejectWithValue }) => {
    try {
      const { page = 1, search = '', carrier_id } = params || {};
      const cacheKey = `${carrier_id}_${page}_${search}_${params.form_tag_id || ''}_${
        params.product_group_id || ''
      }_${params.state || ''}`;
      const { formsCache } = getState().forms;

      if (formsCache[cacheKey] && !params.forceRefresh) {
        return formsCache[cacheKey];
      }

      const { data, headers } = await Api.loadForms(params);
      const formattedData = data.map(v => ({
        ...v,
        pdf_url: v.pdf_file?.url || '',
        name: v.form_name,
        number: v.form_number
      }));
      const pagination = JSON.parse(headers['x-pagination']);

      return {
        data: formattedData,
        pagination,
        cacheKey,
        currentPage: page,
        searchQuery: search,
        formTagId: params.form_tag_id || '',
        productGroupId: params.product_group_id || '',
        stateFilter: params.state || ''
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch forms');
    }
  }
);

export const fetchFormDetails = createAsyncThunk(
  'forms/fetchFormDetails',
  async ({ carrierId, formId, forceRefresh = false }, { getState, rejectWithValue }) => {
    try {
      const cacheKey = `${carrierId}_${formId}`;
      const { detailsCache } = getState().forms;

      if (detailsCache[cacheKey] && !forceRefresh) {
        return detailsCache[cacheKey];
      }
      const [productGroups, formTags, form] = await Promise.all([
        Api.loadProductGroups({ carrier_id: carrierId }),
        Api.loadFormTags({ carrier_id: carrierId }),
        Api.loadForm(formId)
      ]);

      const result = {
        form: { ...form.data, name: form.data.form_name, number: form.data.form_number },
        productGroups: productGroups.data,
        formTags: formTags.data,
        cacheKey
      };

      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch form details');
    }
  }
);

export const toggleFormActiveStatus = createAsyncThunk(
  'forms/toggleActiveStatus',
  async ({ formId, isActive }, { rejectWithValue }) => {
    try {
      await Api.saveForm(formId, { is_active: isActive });
      return { formId, isActive };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to toggle form status');
    }
  }
);

export const fetchFormMetadata = createAsyncThunk(
  'forms/fetchMetadata',
  async (carrierId, { rejectWithValue }) => {
    try {
      const [productGroups, formTags] = await Promise.all([
        Api.loadProductGroups({ carrier_id: carrierId }),
        Api.loadFormTags({ carrier_id: carrierId })
      ]);

      return {
        productGroups: productGroups.data,
        formTags: formTags.data
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch form metadata');
    }
  }
);

export const toggleFormStatus = createAsyncThunk(
  'forms/toggleStatus',
  async ({ formId, isActive }, { rejectWithValue }) => {
    try {
      await Api.saveForm(formId, { is_active: isActive });
      return { formId, isActive };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to toggle form status');
    }
  }
);

export const deleteForm = createAsyncThunk(
  'forms/deleteForm',
  async (formId, { rejectWithValue }) => {
    try {
      await Api.deleteForm(formId);
      return formId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete form');
    }
  }
);

export const exportFormsToCSV = createAsyncThunk(
  'forms/exportCSV',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await Api.loadForms(params);
      // Map API fields to UI keys for consistency
      const formattedData = data.map(v => ({
        ...v,
        pdf_url: v.pdf_file?.url || '',
        name: v.form_name,
        number: v.form_number,
        formattedDate: v.publish_date ? formatInTimeZone(v.publish_date, 'UTC', 'MM/dd/yyyy') : ''
      }));

      const csvData = formattedData.map(f => ({
        'Form Name': f.name,
        'Form Number': f.number,
        'Publish Date': f.formattedDate,
        'Product Groups': f.product_group_ids.join(', '),
        States: f.states.join(', '),
        Tags: f.form_tag_ids.join(', '),
        'Form Description': f.description,
        'PDF File URL': f.pdf_url
      }));

      const csv = Papa.unparse(csvData);

      return csv;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to export forms');
    }
  }
);

const initialState = {
  forms: [],
  formsCache: {},
  detailsCache: {},
  productGroups: [],
  formTags: [],
  pagination: {},
  currentPage: 1,
  searchQuery: '',
  productGroupId: '',
  stateFilter: '',
  formTagId: '',
  loading: false,
  exporting: false,
  error: null,
  successMessage: null,
  currentCarrierId: null,
  currentForm: null,
  detailsLoading: false,
  detailsError: null
};

const formsSlice = createSlice({
  name: 'forms',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      Object.assign(state, action.payload);
    },
    clearCache: state => {
      state.formsCache = {};
      state.detailsCache = {};
    },
    resetForms: state => {
      state.forms = [];
      state.pagination = {};
      state.loading = false;
      state.error = null;
      state.successMessage = null;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    clearMessages: state => {
      state.error = null;
      state.successMessage = null;
    },
    setCurrentCarrier: (state, action) => {
      state.currentCarrierId = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchForms.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchForms.fulfilled, (state, action) => {
        const {
          data,
          pagination,
          cacheKey,
          currentPage,
          searchQuery,
          formTagId,
          productGroupId,
          stateFilter
        } = action.payload;

        state.formsCache[cacheKey] = { data, pagination };
        state.forms = data;
        state.pagination = pagination;
        state.currentPage = currentPage;
        state.searchQuery = searchQuery;
        state.formTagId = formTagId;
        state.productGroupId = productGroupId;
        state.stateFilter = stateFilter;
        state.loading = false;
      })
      .addCase(fetchForms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchFormMetadata.fulfilled, (state, action) => {
        state.productGroups = action.payload.productGroups;
        state.formTags = action.payload.formTags;
      })

      .addCase(toggleFormStatus.fulfilled, (state, action) => {
        const { formId, isActive } = action.payload;
        const form = state.forms.find(f => f.id === formId);

        if (form) {
          form.is_active = isActive;
        }
      })

      .addCase(deleteForm.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteForm.fulfilled, (state, action) => {
        state.forms = state.forms.filter(form => form.id !== action.payload);
        state.loading = false;
        state.successMessage = 'Form successfully deleted';
        state.formsCache = {};
        state.detailsCache = {};
      })
      .addCase(deleteForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(exportFormsToCSV.pending, state => {
        state.exporting = true;
        state.error = null;
      })
      .addCase(exportFormsToCSV.fulfilled, state => {
        state.exporting = false;
      })
      .addCase(exportFormsToCSV.rejected, (state, action) => {
        state.exporting = false;
        state.error = action.payload;
      })

      .addCase(fetchFormDetails.pending, state => {
        state.detailsLoading = true;
        state.detailsError = null;
      })
      .addCase(fetchFormDetails.fulfilled, (state, action) => {
        state.currentForm = action.payload.form;
        state.productGroups = action.payload.productGroups;
        state.formTags = action.payload.formTags;
        state.detailsCache[action.payload.cacheKey] = action.payload;
        state.detailsLoading = false;
      })
      .addCase(fetchFormDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload;
      })

      .addCase(toggleFormActiveStatus.fulfilled, (state, action) => {
        const { formId, isActive } = action.payload;

        if (state.currentForm?.id === formId) {
          state.currentForm.is_active = isActive;
        }
      });
  }
});

export const {
  setFilters,
  clearCache,
  resetForms,
  setSuccessMessage,
  clearMessages,
  setCurrentCarrier,
  setSearchQuery,
  setCurrentPage
} = formsSlice.actions;

export default formsSlice.reducer;
